import React, { useState } from 'react';
import styles from './flightPresentation.module.scss';

/**
 * Presentation helpers shared by the two flight card surfaces —
 * FlightCardNew (chat `flights` messages) and FlightCardWidget (the
 * `flight_list` / `flight_card` widgets). They render the same offer in
 * the same visual language, so the formatting lives in one place rather
 * than being reimplemented on each side.
 */

/** `PT14H30M` → `14h 30m`. Returns the input unchanged if unparseable. */
export const formatDuration = (iso?: string): string => {
    if (!iso) return '--';
    const m = iso.match(/^P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?/i);
    if (!m || (!m[1] && !m[2] && !m[3])) return iso;
    const days = m[1] ? Number(m[1]) : 0;
    const hours = (m[2] ? Number(m[2]) : 0) + days * 24;
    const mins = m[3] ? Number(m[3]) : 0;
    return [hours ? `${hours}h` : '', mins ? `${mins}m` : ''].filter(Boolean).join(' ') || iso;
};

/**
 * ISO duration → total minutes, for sorting. Comparing the raw strings
 * sorts `PT9H` after `PT10H`, which is why this exists.
 */
export const durationMinutes = (iso?: string): number => {
    if (!iso) return Number.POSITIVE_INFINITY;
    const m = iso.match(/^P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?/i);
    if (!m) return Number.POSITIVE_INFINITY;
    const days = m[1] ? Number(m[1]) : 0;
    const hours = m[2] ? Number(m[2]) : 0;
    const mins = m[3] ? Number(m[3]) : 0;
    const total = days * 1440 + hours * 60 + mins;
    return total || Number.POSITIVE_INFINITY;
};

/** `10:00 AM`, in the traveller's own locale-independent 12-hour form. */
export const formatTime = (value?: string): string => {
    if (!value) return '--:--';
    /* Read straight out of the string rather than through Date. A flight
       time belongs to the airport, not to whoever is looking at it: the
       offset in the timestamp is already the airport's, so letting Date
       convert it into the viewer's timezone turns a 10am departure from
       SFO into 9pm for a reader in Yerevan. */
    const m = value.match(/T(\d{2}):(\d{2})/);
    if (m) {
        const raw = Number(m[1]);
        return `${raw % 12 || 12}:${m[2]} ${raw >= 12 ? 'PM' : 'AM'}`;
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

/**
 * Whole days between two timestamps, by their own calendar dates — the
 * `+1` on an overnight arrival. Differencing the instants instead would
 * answer in the viewer's timezone and flip the badge on and off
 * depending on where they happen to be.
 */
export const dayOffset = (from?: string, to?: string): number => {
    const a = from?.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const b = to?.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!a || !b) return 0;
    const start = Date.UTC(Number(a[1]), Number(a[2]) - 1, Number(a[3]));
    const end = Date.UTC(Number(b[1]), Number(b[2]) - 1, Number(b[3]));
    return Math.max(0, Math.round((end - start) / 86400000));
};

/**
 * `2026-09-28T20:54:00-07:00` → `Sep 28, 2026`, read off the string for
 * the same reason as the time: a late-evening departure parsed through
 * `Date` lands on the following day for a reader far enough east, so the
 * page would show the wrong travel date.
 */
export const formatDate = (value?: string): string => {
    if (!value) return '';
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) {
        const d = new Date(value);
        return Number.isNaN(d.getTime())
            ? value
            : d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    }
    /* Formatted through UTC so the parts survive the round trip unshifted. */
    return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString(
        'en-US',
        { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' },
    );
};

/**
 * `2026-09-28T20:54:00-07:00` → `20:54`. The 24-hour form the booking
 * flow uses, where a dense timeline reads better without AM/PM. Same
 * airport-local rule as `formatTime`.
 */
export const formatTime24 = (value?: string): string => {
    if (!value) return '--:--';
    const m = value.match(/T(\d{2}):(\d{2})/);
    if (m) return `${m[1]}:${m[2]}`;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
};

/** `2026-04-30T…` → `Thu, Apr 30`. Weekday and day, no year. */
export const formatDateShort = (value?: string): string => {
    if (!value) return '';
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return formatDate(value);
    return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString(
        'en-US',
        { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' },
    );
};

/**
 * IATA code → the carrier's trading name. Only the carriers we can name
 * with confidence; anything else keeps its code, which is still a valid
 * thing to show a traveller.
 */
const AIRLINE_NAMES: Record<string, string> = {
    AA: 'American Airlines',
    UA: 'United Airlines',
    DL: 'Delta Air Lines',
    BA: 'British Airways',
    LH: 'Lufthansa',
    AF: 'Air France',
    KL: 'KLM',
    IB: 'Iberia',
    LX: 'SWISS',
    AZ: 'ITA Airways',
    EK: 'Emirates',
    QR: 'Qatar Airways',
    TK: 'Turkish Airlines',
    SQ: 'Singapore Airlines',
    NH: 'ANA',
    JL: 'Japan Airlines',
    AC: 'Air Canada',
    QF: 'Qantas',
    VS: 'Virgin Atlantic',
    AY: 'Finnair',
    SK: 'SAS',
    TP: 'TAP Air Portugal',
    EI: 'Aer Lingus',
    FR: 'Ryanair',
    U2: 'easyJet',
    WN: 'Southwest Airlines',
    B6: 'JetBlue',
    AS: 'Alaska Airlines',
};

export const airlineName = (code?: string): string => {
    if (!code) return 'Airline';
    return AIRLINE_NAMES[code.toUpperCase()] || code.toUpperCase();
};

/** Whether two timestamps fall on different calendar dates where they happen. */
export const isDifferentDay = (from?: string, to?: string): boolean => {
    const a = from?.match(/^(\d{4}-\d{2}-\d{2})/);
    const b = to?.match(/^(\d{4}-\d{2}-\d{2})/);
    if (!a || !b) return false;
    return a[1] !== b[1];
};

/** `1530.04` + `USD` → `$1,530`. Falls back to `1530.04 USD` for odd codes. */
export const formatMoney = (value: string | number | undefined, currency = 'USD'): string => {
    if (value === undefined || value === null || value === '') return '';
    const amount = Number(value);
    if (Number.isNaN(amount)) return String(value);
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `${amount} ${currency}`;
    }
};

export const priceAmount = (value: string | number | undefined): number => {
    const n = Number(value);
    return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
};

export const stopsLabel = (stops: number): string =>
    stops === 0 ? 'Non-stop' : `${stops} stop${stops > 1 ? 's' : ''}`;

/**
 * Carrier mark on a white rounded square. Duffel publishes its logos for
 * light grounds only, so the white tile is what keeps a dark-inked mark
 * legible on a dark card. Falls back to the IATA code when the CDN has no
 * logo for that carrier — the common case for the test codes the sandbox
 * returns (ZZ and friends).
 */
export const AirlineLogo: React.FC<{
    code?: string;
    name?: string;
    src?: string;
    size?: 'sm' | 'md';
}> = ({ code, name, src, size = 'md' }) => {
    const [broken, setBroken] = useState(false);
    const url =
        src ||
        (code
            ? `https://assets.duffel.com/img/airlines/for-light-background/full-color-logo/${code}.svg`
            : '');
    const cls = [styles.logo, size === 'sm' && styles.logoSm].filter(Boolean).join(' ');

    if (!url || broken) {
        return (
            <div className={`${cls} ${styles.logoFallback}`}>
                <span className={styles.logoCode}>{code || '—'}</span>
            </div>
        );
    }

    return (
        <div className={cls}>
            <img
                className={styles.logoImg}
                src={url}
                alt={name || code || 'Airline'}
                loading="lazy"
                onError={() => setBroken(true)}
            />
        </div>
    );
};
